import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./Dashboard.css";
import "./TicketsPage.css";
import { API_BASE } from "../config";

const STATUS_META = {
  open: { label: "Aperto", tone: "warning" },
  "in progress": { label: "In lavorazione", tone: "info" },
  closed: { label: "Chiuso", tone: "success" },
};

function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

async function apiFetch(path, { token, headers, ...options } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");

  let payload = null;
  if (isJson) {
    try {
      payload = await res.json();
    } catch {
      payload = null;
    }
  } else {
    try {
      payload = await res.text();
    } catch {
      payload = null;
    }
  }

  if (!res.ok) {
    const msg =
      (payload &&
        typeof payload === "object" &&
        (payload.message || payload.error)) ||
      (typeof payload === "string" && payload) ||
      `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }

  return payload;
}

function Badge({ status }) {
  const meta = STATUS_META[status] || { label: status || "-", tone: "neutral" };
  return <span className={cx("badge", `badge--${meta.tone}`)}>{meta.label}</span>;
}

function Modal({ open, title, onClose, children }) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="modalOverlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 className="modal__title">{title}</h3>
          <button type="button" className="iconBtn" onClick={onClose} aria-label="Chiudi">
            ×
          </button>
        </div>
        <div className="modal__body">{children}</div>
      </div>
    </div>
  );
}

function hasRole(user, role) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  return roles.map((r) => String(r).toLowerCase()).includes(String(role).toLowerCase());
}

function isNotFound(err) {
  return err?.status === 404;
}

function isForbidden(err) {
  return err?.status === 403;
}

function TicketsPage({ token, user }) {
  // ✅ più robusto: usa roles, non user.role
  const isDeveloper = hasRole(user, "developer");
  const canInteract = Boolean(token);

  const [tickets, setTickets] = useState([]);
  const [tenantsMap, setTenantsMap] = useState({});

  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");

  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");

  const [replyMessage, setReplyMessage] = useState("");
  const [replyLoading, setReplyLoading] = useState(false);

  const [statusLoading, setStatusLoading] = useState(false);

  const [toast, setToast] = useState(null); // { type, message }

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(t);
  }, [toast]);

  const dtf = useMemo(
    () =>
      new Intl.DateTimeFormat("it-IT", {
        dateStyle: "short",
        timeStyle: "short",
      }),
    []
  );

  const formatDateTime = useCallback(
    (value) => {
      if (!value) return "";
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? "" : dtf.format(d);
    },
    [dtf]
  );

  const loadTickets = useCallback(
    async (opts = {}) => {
      if (!token) return;
      setListLoading(true);
      setListError("");
      try {
        const data = await apiFetch("/api/tickets", { token, signal: opts.signal });
        setTickets(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err?.name !== "AbortError") {
          // messaggi più chiari
          if (isNotFound(err)) setListError("Endpoint /api/tickets non presente sull’API.");
          else if (isForbidden(err)) setListError("Permessi insufficienti per visualizzare i ticket.");
          else setListError(err?.message || "Errore nel recupero delle richieste");
        }
      } finally {
        setListLoading(false);
      }
    },
    [token]
  );

  const loadTenants = useCallback(
    async (opts = {}) => {
      // ✅ non bloccare la pagina se tenants non esiste / non hai permessi
      if (!token || !isDeveloper) return;
      try {
        const data = await apiFetch("/api/tenants", { token, signal: opts.signal });
        const map = {};
        (Array.isArray(data) ? data : []).forEach((t) => {
          map[t.id] = t.name;
        });
        setTenantsMap(map);
      } catch (err) {
        // ✅ niente toast se 404/403: è "nice to have"
        if (err?.name !== "AbortError") {
          if (!isNotFound(err) && !isForbidden(err)) {
            showToast("error", "Impossibile caricare l’elenco tenant");
          }
        }
      }
    },
    [token, isDeveloper, showToast]
  );

  const loadTicketDetails = useCallback(
    async (ticketId, opts = {}) => {
      if (!token || !ticketId) return;

      setDetailLoading(true);
      setDetailError("");

      // fallback: prendi dalla lista
      const fromList = (Array.isArray(tickets) ? tickets : []).find((t) => String(t.id) === String(ticketId)) || null;

      try {
        // ✅ prova endpoint dettaglio
        const data = await apiFetch(`/api/tickets/${ticketId}`, { token, signal: opts.signal });
        setSelectedTicket(data || fromList);
      } catch (err) {
        if (err?.name !== "AbortError") {
          // ✅ se il backend non ha ancora /api/tickets/:id, non rompere:
          if (isNotFound(err) && fromList) {
            setSelectedTicket(fromList);
            setDetailError(""); // non mostra errore
          } else {
            setDetailError(err?.message || "Errore nel recupero della richiesta");
            setSelectedTicket(null);
          }
        }
      } finally {
        setDetailLoading(false);
      }
    },
    [token, tickets]
  );

  // Caricamento iniziale
  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    loadTickets({ signal: controller.signal });
    loadTenants({ signal: controller.signal });
    return () => controller.abort();
  }, [token, loadTickets, loadTenants]);

  // Dettaglio quando cambia selezione
  useEffect(() => {
    if (!selectedTicketId) {
      setSelectedTicket(null);
      setDetailError("");
      setReplyMessage("");
      return;
    }
    const controller = new AbortController();
    loadTicketDetails(selectedTicketId, { signal: controller.signal });
    return () => controller.abort();
  }, [selectedTicketId, loadTicketDetails]);

  const visibleTickets = useMemo(() => {
    const q = search.trim().toLowerCase();
    const statusWanted = statusFilter;

    const rankStatus = (s) => {
      if (s === "open") return 0;
      if (s === "in progress") return 1;
      if (s === "closed") return 2;
      return 99;
    };

    return (Array.isArray(tickets) ? tickets : [])
      .filter((t) => (statusWanted === "all" ? true : t.status === statusWanted))
      .filter((t) => {
        if (!q) return true;
        const tenantName = isDeveloper ? tenantsMap[t.tenantId] || t.tenantId || "" : "";
        const hay = [
          String(t.id ?? ""),
          String(t.title ?? ""),
          String(t.description ?? ""),
          String(t.status ?? ""),
          String(tenantName ?? ""),
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .slice()
      .sort((a, b) => {
        const ra = rankStatus(a.status);
        const rb = rankStatus(b.status);
        if (ra !== rb) return ra - rb;

        const da = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const db = new Date(b.updatedAt || b.createdAt || 0).getTime();
        if (da && db && da !== db) return db - da;

        const ida = typeof a.id === "number" ? a.id : parseInt(String(a.id), 10);
        const idb = typeof b.id === "number" ? b.id : parseInt(String(b.id), 10);
        if (!Number.isNaN(ida) && !Number.isNaN(idb) && ida !== idb) return idb - ida;

        return String(b.id ?? "").localeCompare(String(a.id ?? ""));
      });
  }, [tickets, search, statusFilter, isDeveloper, tenantsMap]);

  const selectedMessages = useMemo(() => {
    const msgs = selectedTicket?.messages || [];
    return Array.isArray(msgs)
      ? msgs.slice().sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      : [];
  }, [selectedTicket]);

  const currentTicketTenantName = useMemo(() => {
    if (!selectedTicket) return "";
    return tenantsMap[selectedTicket.tenantId] || selectedTicket.tenantId || "";
  }, [selectedTicket, tenantsMap]);

  const isClosed = selectedTicket?.status === "closed";
  const canReply = Boolean(token) && Boolean(selectedTicket?.id) && !isClosed;

  const handleCreate = useCallback(
    async (e) => {
      e.preventDefault();
      setCreateError("");

      const t = createTitle.trim();
      const d = createDescription.trim();
      if (!t || !d) return;

      setCreateLoading(true);
      try {
        const created = await apiFetch("/api/tickets", {
          token,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: t, description: d }),
        });

        setCreateTitle("");
        setCreateDescription("");
        setCreateOpen(false);

        showToast("success", "Richiesta creata con successo");
        await loadTickets();

        if (created?.id) setSelectedTicketId(created.id);
      } catch (err) {
        if (isNotFound(err)) setCreateError("Endpoint /api/tickets non presente sull’API.");
        else setCreateError(err?.message || "Errore durante la creazione della richiesta");
      } finally {
        setCreateLoading(false);
      }
    },
    [createTitle, createDescription, token, loadTickets, showToast]
  );

  const handleSendReply = useCallback(
    async (e) => {
      e.preventDefault();
      if (!selectedTicket?.id) return;

      if (selectedTicket.status === "closed") {
        showToast("error", "Richiesta chiusa: non è possibile rispondere.");
        return;
      }

      const msg = replyMessage.trim();
      if (!msg) return;

      setReplyLoading(true);
      try {
        await apiFetch(`/api/tickets/${selectedTicket.id}/messages`, {
          token,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: msg }),
        });

        setReplyMessage("");
        showToast("success", "Messaggio inviato");

        await loadTicketDetails(selectedTicket.id);
        await loadTickets();
      } catch (err) {
        if (isNotFound(err)) {
          showToast("error", "Funzione messaggi non disponibile sull’API (manca /messages).");
        } else {
          showToast("error", err?.message || "Errore durante l’invio");
        }
      } finally {
        setReplyLoading(false);
      }
    },
    [replyMessage, selectedTicket, token, loadTicketDetails, loadTickets, showToast]
  );

  const handleStatusChange = useCallback(
    async (newStatus) => {
      if (!selectedTicket?.id) return;

      setStatusLoading(true);
      try {
        await apiFetch(`/api/tickets/${selectedTicket.id}/status`, {
          token,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        });

        showToast("success", "Stato aggiornato");
        await loadTicketDetails(selectedTicket.id);
        await loadTickets();
      } catch (err) {
        if (isNotFound(err)) {
          showToast("error", "Cambio stato non disponibile sull’API (manca /status).");
        } else {
          showToast("error", err?.message || "Errore nel cambio stato");
        }
      } finally {
        setStatusLoading(false);
      }
    },
    [selectedTicket, token, loadTicketDetails, loadTickets, showToast]
  );

  const handleReopen = useCallback(async () => {
    if (!isDeveloper || !selectedTicket?.id) return;
    await handleStatusChange("open");
  }, [isDeveloper, selectedTicket, handleStatusChange]);

  return (
    <div className="tickets">
      <div className="tickets__topbar">
        <div className="tickets__titleWrap">
          <h2 className="tickets__title">Assistenza</h2>
          <div className="tickets__subtitle">
            {isDeveloper ? "Vista developer • tutti i tenant" : "Vista utente • le tue richieste"}
          </div>
        </div>

        <div className="tickets__actions">
          <div className="tickets__search">
            <input
              className="input"
              type="search"
              placeholder="Cerca per ID, titolo, descrizione…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={!canInteract}
              aria-label="Cerca richieste"
            />
          </div>

          <select
            className="select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            disabled={!canInteract}
            aria-label="Filtro stato"
          >
            <option value="all">Tutti gli stati</option>
            <option value="open">Aperti</option>
            <option value="in progress">In lavorazione</option>
            <option value="closed">Chiusi</option>
          </select>

          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => loadTickets()}
            disabled={!canInteract || listLoading}
          >
            {listLoading ? "Aggiorno…" : "Ricarica"}
          </button>

          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setCreateOpen(true)}
            disabled={!canInteract}
          >
            Nuova richiesta
          </button>
        </div>
      </div>

      {toast && (
        <div className={cx("toast", `toast--${toast.type}`)} role="status" aria-live="polite">
          {toast.message}
          <button type="button" className="toast__close" onClick={() => setToast(null)} aria-label="Chiudi">
            ×
          </button>
        </div>
      )}

      <div className="tickets__grid">
        {/* LISTA */}
        <section className="card tickets__list">
          <div className="card__header">
            <div>
              <h3 className="card__title">Elenco</h3>
              <div className="card__meta">{visibleTickets.length} richieste</div>
            </div>
          </div>

          {listError && <div className="alert alert--error">{listError}</div>}

          <div className="tableWrap" role="region" aria-label="Elenco richieste assistenza">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>ID</th>
                  {isDeveloper && <th style={{ width: 180 }}>Tenant</th>}
                  <th>Titolo</th>
                  <th style={{ width: 160 }}>Stato</th>
                </tr>
              </thead>
              <tbody>
                {listLoading ? (
                  <>
                    <tr>
                      <td colSpan={isDeveloper ? 4 : 3}><div className="skeletonRow" /></td>
                    </tr>
                    <tr>
                      <td colSpan={isDeveloper ? 4 : 3}><div className="skeletonRow" /></td>
                    </tr>
                    <tr>
                      <td colSpan={isDeveloper ? 4 : 3}><div className="skeletonRow" /></td>
                    </tr>
                  </>
                ) : visibleTickets.length === 0 ? (
                  <tr>
                    <td colSpan={isDeveloper ? 4 : 3}>
                      <div className="empty">
                        <div className="empty__title">Nessuna richiesta trovata</div>
                        <div className="empty__text">Prova a cambiare filtro o crea una nuova richiesta.</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  visibleTickets.map((t) => (
                    <tr
                      key={t.id}
                      className={cx("tableRow", selectedTicketId === t.id && "tableRow--selected")}
                      onClick={() => setSelectedTicketId(t.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") setSelectedTicketId(t.id);
                      }}
                    >
                      <td className="mono">{t.id}</td>
                      {isDeveloper && <td className="muted">{tenantsMap[t.tenantId] || t.tenantId}</td>}
                      <td>
                        <div className="cellTitle">{t.title}</div>
                        {t.description && <div className="cellSub">{t.description}</div>}
                      </td>
                      <td><Badge status={t.status} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* DETTAGLIO */}
        <section className="card tickets__detail">
          {!selectedTicketId ? (
            <div className="empty empty--big">
              <div className="empty__title">Seleziona una richiesta</div>
              <div className="empty__text">Clicca su una richiesta a sinistra per vedere dettagli e conversazione.</div>
            </div>
          ) : detailLoading ? (
            <div className="detailLoading">
              <div className="skeletonLine" style={{ width: "40%" }} />
              <div className="skeletonLine" style={{ width: "80%" }} />
              <div className="skeletonLine" style={{ width: "60%" }} />
              <div className="skeletonBlock" />
              <div className="skeletonBlock" />
            </div>
          ) : detailError ? (
            <div className="empty empty--big">
              <div className="empty__title">Impossibile caricare la richiesta</div>
              <div className="empty__text">{detailError}</div>
              <div style={{ marginTop: 12 }}>
                <button className="btn btn--ghost" type="button" onClick={() => loadTicketDetails(selectedTicketId)}>
                  Riprova
                </button>
                <button className="btn btn--ghost" type="button" onClick={() => setSelectedTicketId(null)} style={{ marginLeft: 8 }}>
                  Chiudi
                </button>
              </div>
            </div>
          ) : !selectedTicket ? (
            <div className="empty empty--big">
              <div className="empty__title">Richiesta non disponibile</div>
              <div className="empty__text">Potrebbe essere stata rimossa o non hai i permessi.</div>
              <button className="btn btn--ghost" type="button" onClick={() => setSelectedTicketId(null)} style={{ marginTop: 12 }}>
                Torna alla lista
              </button>
            </div>
          ) : (
            <div className="detail">
              <div className="detail__header">
                <div className="detail__headerLeft">
                  <div className="detail__kicker">Assistenza</div>

                  <div className="detail__titleRow">
                    <h3 className="detail__title">
                      #{selectedTicket.id} — {selectedTicket.title}
                    </h3>
                    <Badge status={selectedTicket.status} />
                  </div>

                  {isDeveloper && (
                    <div className="detail__meta">
                      <span className="muted">Tenant:</span> {currentTicketTenantName}
                    </div>
                  )}
                </div>

                <div className="detail__headerRight">
                  <button className="btn btn--ghost" type="button" onClick={() => setSelectedTicketId(null)}>
                    Chiudi dettaglio
                  </button>
                </div>
              </div>

              {isClosed && (
                <div className="alert alert--info" style={{ marginTop: 12 }}>
                  {isDeveloper
                    ? "Richiesta chiusa: riaprila per poter rispondere."
                    : "Richiesta chiusa: non puoi più rispondere. Solo l’assistenza può riaprire."}
                </div>
              )}

              <div className="detail__body">
                <div className="detail__section">
                  <div className="detail__sectionTitle">Descrizione</div>
                  <div className="detail__text">{selectedTicket.description}</div>
                </div>

                <div className="detail__section">
                  <div className="detail__sectionTitle">Stato</div>

                  {isDeveloper ? (
                    <div className="row" style={{ gap: 10, alignItems: "center" }}>
                      <select
                        className="select"
                        value={selectedTicket.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        disabled={statusLoading}
                        aria-label="Cambia stato"
                      >
                        <option value="open">open</option>
                        <option value="in progress">in progress</option>
                        <option value="closed">closed</option>
                      </select>

                      {isClosed && (
                        <button type="button" className="btn btn--ghost" onClick={handleReopen} disabled={statusLoading}>
                          {statusLoading ? "Riapro…" : "Riapri"}
                        </button>
                      )}

                      {statusLoading && <span className="muted">Aggiornamento…</span>}
                    </div>
                  ) : (
                    <div className="row">
                      <Badge status={selectedTicket.status} />
                    </div>
                  )}
                </div>

                <div className="detail__section">
                  <div className="detail__sectionTitle">Conversazione</div>

                  {selectedMessages.length === 0 ? (
                    <div className="empty">
                      <div className="empty__title">Nessun messaggio</div>
                      <div className="empty__text">
                        {isNotFound({ status: 404 })
                          ? "La conversazione richiede gli endpoint /messages sul backend."
                          : "Scrivi il primo messaggio per avviare la conversazione."}
                      </div>
                    </div>
                  ) : (
                    <ul className="thread" aria-label="Messaggi">
                      {selectedMessages.map((msg) => {
                        const isRequester = msg.userId === selectedTicket.createdBy;
                        const mine = (isDeveloper && !isRequester) || (!isDeveloper && isRequester);

                        return (
                          <li key={msg.id} className={cx("msg", mine ? "msg--mine" : "msg--theirs")}>
                            <div className="msg__meta">
                              <span className="msg__who">{mine ? "Tu" : isRequester ? "Utente" : "Assistenza"}</span>
                              <span className="msg__time">{formatDateTime(msg.timestamp)}</span>
                            </div>
                            <div className="msg__bubble">{msg.content}</div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              <div className="detail__footer">
                <form onSubmit={handleSendReply} className="reply">
                  <textarea
                    className="textarea"
                    placeholder={
                      isClosed
                        ? isDeveloper
                          ? "Richiesta chiusa: riaprila per rispondere."
                          : "Richiesta chiusa: non puoi più rispondere."
                        : "Scrivi un messaggio…"
                    }
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={3}
                    disabled={replyLoading || !canInteract || isClosed}
                    required
                  />

                  <div className="reply__actions">
                    <div className="muted">
                      {isClosed
                        ? isDeveloper
                          ? "Riapri la richiesta per inviare un messaggio."
                          : "Solo l’assistenza può riaprire la richiesta."
                        : "Invia un aggiornamento alla conversazione."}
                    </div>

                    <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
                      {isDeveloper && isClosed && (
                        <button type="button" className="btn btn--ghost" onClick={handleReopen} disabled={statusLoading}>
                          {statusLoading ? "Riapro…" : "Riapri"}
                        </button>
                      )}

                      <button
                        type="submit"
                        className="btn btn--primary"
                        disabled={replyLoading || !canInteract || isClosed || !replyMessage.trim()}
                      >
                        {replyLoading ? "Invio…" : "Invia"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* MODAL CREAZIONE */}
      <Modal
        open={createOpen}
        title="Nuova richiesta"
        onClose={() => {
          if (createLoading) return;
          setCreateOpen(false);
          setCreateError("");
        }}
      >
        <form onSubmit={handleCreate} className="form">
          {createError && <div className="alert alert--error">{createError}</div>}

          <label className="label">
            Titolo
            <input
              className="input"
              type="text"
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
              placeholder="Es. Errore nella generazione del report mensile"
              maxLength={120}
              required
              disabled={createLoading}
            />
            <div className="hint">{createTitle.length}/120</div>
          </label>

          <label className="label">
            Descrizione
            <textarea
              className="textarea"
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              placeholder="Descrivi cosa succede, come replicarlo e cosa ti aspetti…"
              rows={6}
              maxLength={5000}
              required
              disabled={createLoading}
            />
            <div className="hint">{createDescription.length}/5000</div>
          </label>

          <div className="form__actions">
            <button type="button" className="btn btn--ghost" onClick={() => setCreateOpen(false)} disabled={createLoading}>
              Annulla
            </button>
            <button type="submit" className="btn btn--primary" disabled={createLoading}>
              {createLoading ? "Creo…" : "Crea richiesta"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default TicketsPage;
